import { javaIsBlank, javaParseLong } from '../javaText.ts';
import { SheetEvaluator, optionLabel } from './sheetEvaluator.ts';
import { CONDITIONS_KEY, conditionLabel, decodeConditions, fieldFor, type CharacterSheet, type SheetField, type SheetTemplate } from './sheetModel.ts';

/**
 * A character sheet laid out for paper, as the desktop's `sheets.SheetPrint` lays it out
 * for its PDF export: the name and the numbers a GM looks for first at the top, each section
 * a framed table, numbers in boxes (worked-out ones shaded), text on ruled lines, checkboxes
 * and small resources drawn as boxes to tick, rolls with their numbers filled in, and the
 * licence at the foot. A template prints blank too, every box empty, to fill in by hand. The
 * web prints it through the browser, styled by the same stylesheet
 * (src/main/resources/print/sheet-print.css), on the paper Settings says;
 * schema/fixtures/sheet-cases.json holds the desktop's pages, and these must match them.
 */

export type Paper = 'A4' | 'LETTER';
export const PAPERS: Readonly<Record<Paper, string>> = { A4: 'A4', LETTER: 'US Letter' };

/** A resource with a maximum this small prints as boxes to tick (hit points never do). */
const MAX_TRACK = 10n;

/** The class that names the page the sheet is printed on. */
export const paperClass = (paper: Paper) => (paper === 'LETTER' ? 'sp-letter' : 'sp-a4');

/** A character's printed sheet. `entryTitle` is the title of the entry it belongs to, or null. */
export function printSheet(template: SheetTemplate, sheet: CharacterSheet, entryTitle: string | null, paper: Paper): string {
  return page(template, sheet, entryTitle, paper);
}

/** A template's blank sheet: every box empty, the choices and conditions listed to tick. */
export function printBlank(template: SheetTemplate, paper: Paper): string {
  return page(template, null, null, paper);
}

function page(template: SheetTemplate, sheet: CharacterSheet | null, entryTitle: string | null, paper: Paper): string {
  const values = new SheetEvaluator(template, sheet?.values ?? null);
  let out = `<div class="sheet-print ${paperClass(paper)}">\n`;

  const sub =
    template.name +
    (javaIsBlank(template.system) ? '' : ` · ${template.system}`) +
    (!sheet || entryTitle === null || javaIsBlank(entryTitle) || entryTitle === sheet.name ? '' : ` · ${entryTitle}`);
  out += '<table class="sp-head"><tr><td>';
  out += sheet ? `<div class="sp-name">${escape(sheet.name)}</div>` : '<div class="sp-name sp-line"></div><div class="sp-label">Character name</div>';
  out += `<div class="sp-sub">${escape(sub)}</div></td><td class="sp-stats">`;
  const hitPoints = fieldFor(template, 'HIT_POINTS');
  const defense = fieldFor(template, 'DEFENSE');
  const initiative = fieldFor(template, 'INITIATIVE');
  if (!sheet) {
    if (hitPoints && hitPoints.type === 'RESOURCE') out += stat('', hitPoints.label);
    if (defense) out += stat('', defense.label);
    if (initiative) out += stat('', initiative.label);
  } else {
    const hp = values.hitPoints();
    if (hp) out += stat(`${hp[0]}/${hp[1]}`, hitPoints?.label ?? 'HP');
    const d = defense && values.number(defense.key);
    if (defense && d !== null && d !== undefined) out += stat(String(d), defense.label);
    const i = initiative && values.number(initiative.key);
    if (initiative && i !== null && i !== undefined) out += stat(signed(initiative, i), initiative.label);
  }
  out += '</td></tr></table>\n';

  if (!sheet) {
    if (template.conditions.length > 0) {
      out += '<div class="sp-conditions"><span class="sp-label">Conditions</span>';
      for (const c of template.conditions) out += `<span class="sp-option">${tick(false)}${escape(c)}</span>`;
      out += '</div>\n';
    }
  } else {
    const conditions = decodeConditions(sheet.values.get(CONDITIONS_KEY));
    if (conditions.length > 0) out += `<div class="sp-conditions"><span class="sp-label">Conditions</span>${escape(conditions.map(conditionLabel).join(' · '))}</div>\n`;
  }

  for (const section of template.sections) {
    // One table a section: its heading a header row, which stays with the rows below it and
    // is printed again at the top of the next page when a long section runs on.
    const columns = section.columns;
    out += '<table class="sp-section">';
    if (!javaIsBlank(section.title)) out += `<thead><tr><th${columns > 1 ? ` colspan="${columns}"` : ''}>${escape(section.title)}</th></tr></thead>`;
    out += '<tbody>';
    // Laid out as on screen: across the columns, a long text on a row of its own.
    let column = 0;
    for (const field of section.fields) {
      const wide = field.type === 'LONG_TEXT';
      if (column > 0 && (wide || column >= columns)) {
        out += `${emptyCells(columns - column)}</tr>`;
        column = 0;
      }
      if (column === 0) out += '<tr>';
      out += wide && columns > 1 ? `<td colspan="${columns}">` : '<td>';
      out += sheet ? cell(template, field, values) : blankCell(template, field);
      out += '</td>';
      column = wide ? columns : column + 1;
    }
    if (column > 0) out += `${emptyCells(columns - column)}</tr>`;
    out += '</tbody></table>\n';
  }

  if (!javaIsBlank(template.license)) out += `<div class="sp-license">${escape(`${template.name} · ${template.license}`)}</div>\n`;
  return `${out}</div>\n`;
}

function cell(template: SheetTemplate, field: SheetField, values: SheetEvaluator): string {
  const key = field.key;
  let out: string;
  switch (field.type) {
    case 'NUMBER': {
      const value = values.number(key);
      out = box(value !== null ? signed(field, value) : values.raw(key), false);
      break;
    }
    case 'DERIVED': {
      const value = values.number(key);
      out = box(value !== null ? signed(field, value) : '—', true);
      break;
    }
    case 'TEXT':
    case 'CHOICE':
      out = `<div class="sp-line">${escape(field.type === 'CHOICE' ? values.choiceLabel(key) : values.raw(key))}</div>`;
      break;
    case 'LONG_TEXT':
      out = `<div class="sp-text">${escape(values.raw(key))}</div>`;
      break;
    case 'DICE':
      out = `<div class="sp-line sp-roll">${escape(filledIn(values.raw(key), values))}</div>`;
      break;
    case 'CHECKBOX':
      return `<div class="sp-check">${tick(values.isChecked(key))}${escape(field.label)}</div>`;
    case 'RESOURCE':
      out = resource(template, field, values);
      break;
  }
  return out + label(field.label);
}

/** Every box empty; a choice's options listed under it, and a fixed small maximum drawn as boxes to tick. */
function blankCell(template: SheetTemplate, field: SheetField): string {
  switch (field.type) {
    case 'NUMBER':
      return box('', false) + label(field.label);
    case 'DERIVED':
      return box('', true) + label(field.label);
    case 'TEXT':
    case 'DICE':
      return '<div class="sp-line"></div>' + label(field.label);
    case 'CHOICE':
      return `<div class="sp-line"></div>${label(field.label)}<div class="sp-options">${escape(field.options.map(optionLabel).join(' / '))}</div>`;
    case 'LONG_TEXT':
      return '<div class="sp-text"></div>' + label(field.label);
    case 'CHECKBOX':
      return `<div class="sp-check">${tick(false)}${escape(field.label)}</div>`;
    case 'RESOURCE': {
      const max = javaParseLong(field.formula) ?? 0n;
      const out = !isHitPoints(template, field) && max >= 1n && max <= MAX_TRACK ? `<div class="sp-track">${tick(false).repeat(Number(max))}</div>` : resourceBoxes('', '');
      return out + label(field.label);
    }
  }
}

function resource(template: SheetTemplate, field: SheetField, values: SheetEvaluator): string {
  const current = values.number(field.key);
  const max = values.max(field.key);
  if (!isHitPoints(template, field) && max !== null && max >= 1n && max <= MAX_TRACK && current !== null) {
    let ticks = '';
    for (let i = 1n; i <= max; i++) ticks += tick(i <= current);
    return `<div class="sp-track">${ticks}</div>`;
  }
  return resourceBoxes(current !== null ? String(current) : '', max !== null ? String(max) : '');
}

const isHitPoints = (template: SheetTemplate, field: SheetField) => fieldFor(template, 'HIT_POINTS')?.key === field.key;

const resourceBoxes = (current: string, max: string) =>
  `<table class="sp-resource"><tr><td><div class="sp-box">${current}</div></td><td class="sp-of">of</td><td><div class="sp-box sp-derived">${max}</div></td></tr></table>`;

/** Java's `\s` (ASCII whitespace), as `Pattern` reads it. */
const JAVA_SPACE = '[ \\t\\n\\x0B\\f\\r]';

/** A bonus its roll adds (`1d20 + @athletics`, `4dF + @careful`) reads "+2"; any other number reads "2". */
function signed(field: SheetField, value: bigint): string {
  const bonus = new RegExp(`\\+${JAVA_SPACE}*@${field.key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![A-Za-z0-9_])`).test(field.roll);
  return `${bonus && value >= 0n ? '+' : ''}${value}`;
}

/** "1d20 + @str_mod + @prof" -> "1d20 + 4 + 3": a roll as the player would read it off the sheet. */
export function filledIn(roll: string, values: SheetEvaluator): string {
  return roll.replace(/@([A-Za-z_][A-Za-z0-9_]*)/g, (whole, key: string) => {
    const value = values.number(key);
    return value !== null ? String(value) : whole;
  });
}

const box = (value: string, derived: boolean) => `${derived ? '<div class="sp-box sp-derived">' : '<div class="sp-box">'}${escape(value)}</div>`;
const label = (text: string) => `<div class="sp-label">${escape(text)}</div>`;
const stat = (value: string, text: string) => `<div class="sp-stat"><div class="sp-stat-value">${escape(value)}</div><div class="sp-label">${escape(text)}</div></div>`;
const tick = (on: boolean) => (on ? '<span class="sp-tick sp-on"></span>' : '<span class="sp-tick"></span>');
const emptyCells = (count: number) => '<td></td>'.repeat(Math.max(0, count));

function escape(text: string): string {
  return text.replace(/[&<>"]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;'));
}
